import firebase from 'firebase/app'
import 'firebase/auth'
import {
  getCurrentFirebaseUser,
  requireFirebaseApiKey,
  requireFirebaseAuth,
} from './client'
import { getHanaspeakApiBaseUrl } from './functions-api'

const deviceIdStorageKey = 'hanaspeak.singleDeviceSession.deviceId'

export interface SessionResult {
  readonly customToken: string
  readonly mfaEnrollmentRequired: boolean
  readonly mfaRequired: false
  readonly sessionId: string
}

export interface MfaRequiredResult {
  readonly expiresInSeconds?: number
  readonly firstFactor?: string
  readonly mfaChallengeToken: string
  readonly mfaRequired: true
}

export interface OnboardingRequiredResult {
  readonly mfaRequired?: never
  readonly onboardingRequired: true
}

export type CompletedFirstFactorResult = SessionResult | MfaRequiredResult
export type FirstFactorResult = CompletedFirstFactorResult | OnboardingRequiredResult

export interface GoogleOnboardingInput {
  readonly firstname: string
  readonly lastname: string
  readonly middlename: string
}

export interface GoogleOnboardingIdentity {
  readonly email: string
}

export interface EmailOtpRequestResult {
  readonly accepted: boolean
  readonly cooldownSeconds: number
  readonly expiresInSeconds: number
  readonly maskedEmail: string
  readonly requestId: string
}

export interface MfaStatusResult {
  readonly hasPendingSetup: boolean
  readonly method: 'TOTP' | null
  readonly mfaEnabled: boolean
  readonly requiresMfaEnrollment: boolean
}

export interface TotpSetupResult {
  readonly accountName: string
  readonly issuer: string
  readonly manualKey: string
  readonly qrCodeDataUrl: string
}

export interface ExternalIdentity {
  readonly email: string | null
  readonly provider: 'GOOGLE'
}

interface PasswordVerificationResponse {
  readonly error?: {
    readonly message?: unknown
  }
  readonly idToken?: unknown
}

function createBrowserDeviceId() {
  const cryptoApi = typeof window !== 'undefined'
    ? window.crypto as Crypto & { readonly randomUUID?: () => string }
    : null

  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID()
  }

  if (typeof cryptoApi?.getRandomValues === 'function') {
    const randomValues = new Uint32Array(4)
    cryptoApi.getRandomValues(randomValues)

    return Array.from(randomValues, (value) => value.toString(16).padStart(8, '0')).join('-')
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

function getBrowserDeviceId() {
  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    const storedDeviceId = window.localStorage.getItem(deviceIdStorageKey)

    if (storedDeviceId) {
      return storedDeviceId
    }

    const nextDeviceId = createBrowserDeviceId()
    window.localStorage.setItem(deviceIdStorageKey, nextDeviceId)

    return nextDeviceId
  } catch {
    return undefined
  }
}

function getBrowserDeviceLabel() {
  if (typeof window === 'undefined') {
    return 'Web browser'
  }

  return [window.navigator.platform, window.navigator.language]
    .filter((value) => value.trim().length > 0)
    .join(' / ') || 'Web browser'
}

function withDeviceMetadata<T extends Record<string, unknown>>(body: T) {
  return {
    ...body,
    deviceId: getBrowserDeviceId(),
    deviceLabel: getBrowserDeviceLabel(),
  }
}

async function parseApiJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  const payload = await response.json().catch(() => null) as { readonly error?: unknown } | null

  if (!response.ok) {
    const message = typeof payload?.error === 'string' && payload.error.trim().length > 0
      ? payload.error
      : fallbackMessage
    throw new Error(message)
  }

  return payload as T
}

async function apiJson<T>(path: string, options: RequestInit = {}, fallbackMessage = 'Unable to complete this request right now.') {
  const response = await fetch(`${getHanaspeakApiBaseUrl()}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  })

  return parseApiJson<T>(response, fallbackMessage)
}

async function authenticatedApiJson<T>(path: string, options: RequestInit = {}, fallbackMessage?: string) {
  const user = getCurrentFirebaseUser()

  if (!user) {
    throw new Error('Please sign in again to continue.')
  }

  const idToken = await user.getIdToken(true)

  return apiJson<T>(path, {
    ...options,
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(options.headers ?? {}),
    },
  }, fallbackMessage)
}

function getPasswordVerificationErrorMessage(
  payload: PasswordVerificationResponse | null,
) {
  switch (payload?.error?.message) {
    case 'EMAIL_NOT_FOUND':
    case 'INVALID_LOGIN_CREDENTIALS':
    case 'INVALID_PASSWORD':
      return 'Invalid email or password.'
    case 'USER_DISABLED':
      return 'This account has been disabled.'
    default:
      return 'Unable to sign in right now. Please try again.'
  }
}

async function verifyPasswordWithoutChangingClientAuth(email: string, password: string) {
  const response = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${encodeURIComponent(requireFirebaseApiKey())}`,
    {
      body: JSON.stringify({
        email,
        password,
        returnSecureToken: true,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    },
  )
  const payload = await response.json().catch(() => null) as PasswordVerificationResponse | null

  if (!response.ok) {
    throw new Error(getPasswordVerificationErrorMessage(payload))
  }

  if (typeof payload?.idToken !== 'string' || payload.idToken.length === 0) {
    throw new Error('Password verification did not return an identity token.')
  }

  return payload.idToken
}

function isSessionResult(result: FirstFactorResult): result is SessionResult {
  return result.mfaRequired === false
}

export function isOnboardingRequiredResult(
  result: FirstFactorResult,
): result is OnboardingRequiredResult {
  return 'onboardingRequired' in result && result.onboardingRequired === true
}

function assertCompletedFirstFactorResult(
  result: FirstFactorResult,
): CompletedFirstFactorResult {
  if (isOnboardingRequiredResult(result)) {
    throw new Error('This sign-in method requires Google account completion.')
  }

  return result
}

async function signInWithSession(result: FirstFactorResult) {
  const auth = requireFirebaseAuth()
  if (isOnboardingRequiredResult(result)) {
    return result
  }

  if (!isSessionResult(result)) {
    await auth.signOut().catch(() => undefined)
    return result
  }

  const credential = await auth.signInWithCustomToken(result.customToken)
  const sessionUser = credential.user ?? auth.currentUser
  const tokenResult = await sessionUser?.getIdTokenResult(true)

  if (tokenResult?.claims.sid !== result.sessionId) {
    await auth.signOut().catch(() => undefined)
    throw new Error('Secure login session did not include the expected session claim.')
  }

  return result
}

async function completeFirstFactor(
  firstFactor: 'PASSWORD' | 'GOOGLE',
  identityToken?: string,
) {
  const options: RequestInit = {
    body: JSON.stringify(withDeviceMetadata({ firstFactor })),
    method: 'POST',
  }

  if (identityToken) {
    return apiJson<FirstFactorResult>('/auth/first-factor/complete', {
      ...options,
      headers: {
        Authorization: `Bearer ${identityToken}`,
      },
    }, 'Unable to complete sign-in right now.')
  }

  return authenticatedApiJson<FirstFactorResult>(
    '/auth/first-factor/complete',
    options,
    'Unable to complete sign-in right now.',
  )
}

export async function loginWithPasswordFirstFactor(email: string, password: string) {
  const auth = requireFirebaseAuth()

  try {
    const identityToken = await verifyPasswordWithoutChangingClientAuth(email, password)
    const firstFactorResult = await completeFirstFactor('PASSWORD', identityToken)
    if (isOnboardingRequiredResult(firstFactorResult)) {
      await auth.signInWithEmailAndPassword(email, password)
      return firstFactorResult
    }
    return assertCompletedFirstFactorResult(await signInWithSession(firstFactorResult))
  } catch (error) {
    await auth.signOut().catch(() => undefined)
    throw error
  }
}

export async function loginWithGoogleFirstFactor() {
  const auth = requireFirebaseAuth()
  const provider = new firebase.auth.GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })

  try {
    await auth.signInWithPopup(provider)
    return signInWithSession(await completeFirstFactor('GOOGLE'))
  } catch (error) {
    await auth.signOut().catch(() => undefined)
    throw error
  }
}

export function getGoogleOnboardingIdentity(): GoogleOnboardingIdentity | null {
  const user = getCurrentFirebaseUser()
  const email = user?.email?.trim()
  const hasGoogleIdentity = user?.providerData.some(
    (provider) => provider?.providerId === 'google.com',
  )

  return email && hasGoogleIdentity ? { email } : null
}

export function getPasswordOnboardingIdentity(): GoogleOnboardingIdentity | null {
  const user = getCurrentFirebaseUser()
  const email = user?.email?.trim()
  return email ? { email } : null
}

export async function completePasswordOnboarding(input: GoogleOnboardingInput) {
  await authenticatedApiJson<{ created: boolean }>('/auth/password/profile/complete', {
    body: JSON.stringify(input), method: 'POST',
  }, 'Unable to complete account setup right now.')
  const result = await completeFirstFactor('PASSWORD')
  return assertCompletedFirstFactorResult(await signInWithSession(result))
}

export async function completeGoogleOnboarding(input: GoogleOnboardingInput) {
  const result = await authenticatedApiJson<CompletedFirstFactorResult>(
    '/auth/google/onboarding/complete',
    {
      body: JSON.stringify(withDeviceMetadata({
        firstname: input.firstname,
        lastname: input.lastname,
        middlename: input.middlename,
      })),
      method: 'POST',
    },
    'Unable to complete Google account setup right now.',
  )

  return assertCompletedFirstFactorResult(await signInWithSession(result))
}

export function requestEmailOtp(email: string) {
  return apiJson<EmailOtpRequestResult>('/auth/email-otp/request', {
    body: JSON.stringify({ email }),
    method: 'POST',
  }, 'Unable to send a sign-in code right now.')
}

export async function verifyEmailOtp(requestId: string, code: string) {
  const result = await apiJson<FirstFactorResult>('/auth/email-otp/verify', {
    body: JSON.stringify(withDeviceMetadata({ code, requestId })),
    method: 'POST',
  }, 'Unable to complete sign-in right now.')

  return assertCompletedFirstFactorResult(await signInWithSession(result))
}

export async function verifyMfaChallenge(mfaChallengeToken: string, code: string) {
  const result = await apiJson<SessionResult>('/auth/mfa/challenge/verify', {
    body: JSON.stringify({ code, mfaChallengeToken }),
    method: 'POST',
  }, 'Unable to complete sign-in right now.')
  const signedResult = await signInWithSession(result)

  if (!isSessionResult(signedResult)) {
    throw new Error('Authenticator verification did not complete the secure session.')
  }

  return signedResult
}

export function getMfaStatus() {
  return authenticatedApiJson<MfaStatusResult>('/auth/mfa/status')
}

export function startTotpSetup() {
  return authenticatedApiJson<TotpSetupResult>('/auth/mfa/totp/setup', { method: 'POST' }, 'Unable to start authenticator setup right now.')
}

export function verifyTotpSetup(firstCode: string, secondCode: string) {
  return authenticatedApiJson<{ readonly mfaEnabled: boolean }>('/auth/mfa/totp/verify', {
    body: JSON.stringify({ firstCode, secondCode }),
    method: 'POST',
  }, 'Unable to enable authenticator MFA right now.')
}

export function cancelTotpSetup() {
  return authenticatedApiJson<{ readonly cancelled: boolean }>('/auth/mfa/totp/cancel', { method: 'POST' })
}

export function disableTotpMfa(code: string) {
  return authenticatedApiJson<{ readonly mfaEnabled: boolean }>('/auth/mfa/disable', {
    body: JSON.stringify({ code }),
    method: 'POST',
  }, 'Unable to disable authenticator MFA right now.')
}

export async function listExternalIdentities() {
  const result = await authenticatedApiJson<{ readonly identities: readonly ExternalIdentity[] }>('/auth/external-identities')
  return result.identities
}

export async function linkGoogleAccount() {
  const user = getCurrentFirebaseUser()

  if (!user) {
    throw new Error('Please sign in again to continue.')
  }

  const provider = new firebase.auth.GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  await user.linkWithPopup(provider)
  await user.getIdToken(true)

  return authenticatedApiJson<{ readonly linked: boolean; readonly provider: 'GOOGLE' }>('/auth/google/link', {
    method: 'POST',
  }, 'Unable to link Google right now.')
}

export async function unlinkGoogleAccount() {
  const user = getCurrentFirebaseUser()

  if (!user) {
    throw new Error('Please sign in again to continue.')
  }

  await authenticatedApiJson<{ readonly provider: 'GOOGLE'; readonly unlinked: boolean }>('/auth/external-identities/google', {
    method: 'DELETE',
  }, 'Unable to unlink Google right now.')

  await user.unlink('google.com').catch((error: unknown) => {
    const message = error instanceof Error ? error.message : 'Unable to unlink Google from Firebase Auth.'
    throw new Error(message)
  })
}

export function revokeCurrentSession() {
  return authenticatedApiJson<{ readonly revoked: boolean }>('/auth/sessions/revoke', { method: 'POST' })
}
